import { Navigate, createBrowserRouter, useParams } from "react-router";

import { routerBasename } from "./lib/auth-flow.js";
import { AppGate } from "./components/PlatformShell";
import { RequireAuth } from "./components/RequireAuth";
import { KeyAi } from "./routes/KeyAi";
import { Aliases } from "./routes/Aliases";
import { Forwarders } from "./routes/Forwarders";
import { Migration } from "./routes/Migration";
import { MigrationNew } from "./routes/MigrationNew";
import { NotFound } from "./routes/NotFound";
import { Overview } from "./routes/Overview";
import { AcceptInvitation } from "./routes/auth/AcceptInvitation";
import { ForgotPassword } from "./routes/auth/ForgotPassword";
import { Register } from "./routes/auth/Register";
import { ResetPassword } from "./routes/auth/ResetPassword";
import { SignIn } from "./routes/auth/SignIn";
import { TwoFactor } from "./routes/auth/TwoFactor";
import { VerifyEmail } from "./routes/auth/VerifyEmail";
import { DomainDetail } from "./routes/domains/DomainDetail";
import { DomainNew } from "./routes/domains/DomainNew";
import { DomainRemove } from "./routes/domains/DomainRemove";
import { DomainsList } from "./routes/domains/DomainsList";
import { MailboxDetail } from "./routes/mailboxes/MailboxDetail";
import { MailboxNew } from "./routes/mailboxes/MailboxNew";
import { MailboxesList } from "./routes/mailboxes/MailboxesList";
import { Settings } from "./routes/settings/Settings";
import { AbusePage, AuditPage, CaddyNote, MailHealthPage, QueuesPage, ServerSettingsPage } from "./routes/shell/PlatformPages";
import { HUB_PAGES, HubHome, HubLegacyWorkRedirect, HubMine, HubSection, HubStaffPageGate, HubTeamTasksGate, MY_HUB_PAGES, SiteDatabases, SiteList, SiteOverview, SiteRedirects } from "./routes/shell/RecordPages";
import { SiteFiles } from "./routes/shell/SiteFiles";
import { Billing } from "./routes/Billing";
import {
	AccessPage,
	ConfigPage,
	MailHome,
	WorkspaceAppsPage,
	WorkspacesPage,
} from "./routes/shell/WorkspacePages";
import { TeamChannels, TeamOverview } from "./routes/shell/TeamPages";
import { SocialCalendar, SocialChannels, SocialCompose, SocialOverview, SocialResults } from "./routes/shell/SocialPages";
import { EngageAccounts, EngageConversation, EngageInbox, EngageLinkedInSignIn, EngagePolicy } from "./routes/shell/EngagePages";

function Legacy({ to }: { to: string }) {
	const params = useParams();
	const rest = params["*"] ?? "";
	return <Navigate to={rest ? `${to}/${rest}` : to} replace />;
}

const handle = (title: string) => ({ handle: { title } });

const basename = routerBasename();

export const router = createBrowserRouter([
	{ path: "/signin", element: <SignIn /> },
	{ path: "/register", element: <Register /> },
	{ path: "/verify-email", element: <VerifyEmail /> },
	{ path: "/forgot-password", element: <ForgotPassword /> },
	{ path: "/reset-password", element: <ResetPassword /> },
	{ path: "/invite", element: <AcceptInvitation /> },
	{ path: "/2fa", element: <TwoFactor /> },
	{ path: "/manage/signin", element: <Navigate to="/signin" replace /> },
	{ path: "/manage/register", element: <Navigate to="/register" replace /> },
	{ path: "/mail/signin", element: <Navigate to="/signin" replace /> },
	{ path: "/dash/signin", element: <Navigate to="/signin" replace /> },
	{
		element: <RequireAuth />,
		children: [
			{ path: "/", element: <Navigate to="/manage" replace /> },
			{ path: "/ai", element: <KeyAi />, ...handle("Key AI") },
			{ path: "/manage", element: <Overview />, ...handle("Overview") },
			{ path: "/manage/workspaces", element: <WorkspacesPage />, ...handle("Workspaces") },
			{ path: "/manage/workspaces/:organisationId", element: <WorkspaceAppsPage />, ...handle("Apps") },
			{ path: "/manage/access", element: <AccessPage />, ...handle("Access") },
			{ path: "/manage/domains", element: <DomainsList />, ...handle("Domains") },
			{ path: "/manage/domains/new", element: <DomainNew />, ...handle("Add domain") },
			{ path: "/manage/domains/:id", element: <DomainDetail />, ...handle("Domain") },
			{ path: "/manage/domains/:id/remove", element: <DomainRemove />, ...handle("Remove domain") },
			{ path: "/manage/billing", element: <Billing />, ...handle("Billing") },
			{ path: "/manage/config", element: <ConfigPage />, ...handle("Config") },
			{ path: "/manage/account", element: <Settings />, ...handle("Account") },
			{ path: "/manage/account/:section", element: <Settings />, ...handle("Account") },
			{ path: "/manage/platform", element: <Navigate to="/manage/platform/mail/health" replace /> },
			{ path: "/manage/platform/mail/health", element: <MailHealthPage />, ...handle("Health") },
			{ path: "/manage/platform/mail/queues", element: <QueuesPage />, ...handle("Queues") },
			{ path: "/manage/platform/mail/abuse", element: <AbusePage />, ...handle("Abuse") },
			{ path: "/manage/platform/mail/server", element: <ServerSettingsPage />, ...handle("Server settings") },
			{ path: "/manage/platform/audit", element: <AuditPage />, ...handle("Audit") },
			{ path: "/manage/platform/caddy/routes", element: <CaddyNote topic="Routes and HTTPS" />, ...handle("Routes") },
			{ path: "/manage/platform/caddy/proxy", element: <CaddyNote topic="Proxy settings" />, ...handle("Proxy") },
			{ path: "/manage/platform/caddy/logs", element: <CaddyNote topic="Logs" />, ...handle("Logs") },
			{ path: "/manage/platform/caddy/service", element: <CaddyNote topic="The Caddy service" />, ...handle("Service") },

			{
				path: "/hub",
				element: <AppGate app="hub" />,
				children: [
					{ index: true, element: <HubHome />, ...handle("Overview") },
					...MY_HUB_PAGES.map((page) => ({
						path: page.path,
						element: page.path === "my/tasks" ? <HubSection id="tasks" /> : <HubMine title={page.title} />,
						...handle(page.title),
					})),
					...HUB_PAGES.flatMap((page) => {
						const detail = page.path === "quotations"
							|| page.path === "sales-orders"
							|| page.path === "invoices"
							|| page.path === "receipts"
							|| page.path === "bills"
							|| page.path === "purchase-orders"
							|| page.path === "credits"
							|| page.path === "customer-sites"
							|| page.path === "projects"
							|| page.path === "inventory"
							|| page.path === "consumables";
						const pageElement = page.path === "tasks"
							? (
								<HubTeamTasksGate>
									<HubSection id={page.path} />
								</HubTeamTasksGate>
							)
							: (
								<HubStaffPageGate>
									<HubSection id={page.path} />
								</HubStaffPageGate>
							);
						const routes = detail
							? [
								...(page.path === "customer-sites" || page.path === "projects" || page.path === "inventory" || page.path === "consumables"
									? [{ path: `${page.path}/:docId`, element: <HubStaffPageGate><HubSection id={page.path} /></HubStaffPageGate>, ...handle(page.title) }]
									: [
										{ path: `${page.path}/:docId/edit`, element: <HubStaffPageGate><HubSection id={page.path} /></HubStaffPageGate>, ...handle(page.title) },
										{ path: `${page.path}/:docId`, element: <HubStaffPageGate><HubSection id={page.path} /></HubStaffPageGate>, ...handle(page.title) },
									]),
							]
							: [];
						return [...routes, { path: page.path, element: pageElement, ...handle(page.title) }];
					}),
					{ path: "work", element: <HubLegacyWorkRedirect /> },
					{ path: "operations", element: <Navigate to="/hub/inventory" replace /> },
					{ path: "sales", element: <Navigate to="/hub/leads" replace /> },
					{ path: "purchases", element: <Navigate to="/hub/expenses" replace /> },
					{ path: "people", element: <Navigate to="/hub/leave" replace /> },
					{ path: "finance", element: <Navigate to="/hub/transactions" replace /> },
					{ path: "admin", element: <Navigate to="/hub/maintenance" replace /> },
				],
			},

			{
				path: "/mail",
				element: <AppGate app="mail" />,
				children: [
					{ index: true, element: <MailHome />, ...handle("Overview") },
					{ path: "mailboxes", element: <MailboxesList />, ...handle("Mailboxes") },
					{ path: "mailboxes/new", element: <MailboxNew />, ...handle("New mailbox") },
					{ path: "mailboxes/:id", element: <MailboxDetail />, ...handle("Mailbox") },
					{ path: "aliases", element: <Aliases />, ...handle("Aliases") },
					{ path: "forwarders", element: <Forwarders />, ...handle("Forwarders") },
					{ path: "migration", element: <Migration />, ...handle("Migration") },
					{ path: "migration/new", element: <MigrationNew />, ...handle("Set up a migration") },
				],
			},

			{
				path: "/site",
				element: <AppGate app="site" />,
				children: [
					{ index: true, element: <SiteOverview />, ...handle("Overview") },
					{ path: "sites", element: <SiteList />, ...handle("Sites") },
					{ path: "sites/:siteId/files", element: <SiteFiles />, ...handle("Files") },
					{ path: "databases", element: <SiteDatabases />, ...handle("Databases") },
					{ path: "redirects", element: <SiteRedirects />, ...handle("Redirects") },
				],
			},

			{
				path: "/team",
				element: <AppGate app="team" />,
				children: [
					{
						element: <TeamOverview />,
						children: [
							{ index: true, ...handle("Team") },
							{ path: "channels", element: <TeamChannels /> },
							{ path: "channels/:id", ...handle("Team") },
						],
					},
				],
			},

			{
				path: "/social",
				element: <AppGate app="social" />,
				children: [
					{ index: true, element: <SocialOverview />, ...handle("Overview") },
					{ path: "channels", element: <SocialChannels />, ...handle("Channels") },
					{ path: "calendar", element: <SocialCalendar />, ...handle("Calendar") },
					{ path: "compose", element: <SocialCompose />, ...handle("Compose") },
					{ path: "results", element: <SocialResults />, ...handle("Results") },
				],
			},

			{
				path: "/engage",
				element: <AppGate app="engage" />,
				children: [
					{ index: true, element: <Navigate to="inbox" replace />, ...handle("Inbox") },
					{ path: "inbox", element: <EngageInbox />, ...handle("Inbox") },
					{ path: "inbox/:id", element: <EngageConversation />, ...handle("Chat") },
					{ path: "accounts", element: <EngageAccounts />, ...handle("Accounts") },
					{ path: "linkedin-sign-in/:loginId", element: <EngageLinkedInSignIn />, ...handle("LinkedIn") },
					{ path: "policy", element: <EngagePolicy />, ...handle("Policy") },
				],
			},

			{ path: "/domains/*", element: <Legacy to="/manage/domains" /> },
			{ path: "/domains", element: <Navigate to="/manage/domains" replace /> },
			{ path: "/mailboxes/*", element: <Legacy to="/mail/mailboxes" /> },
			{ path: "/mailboxes", element: <Navigate to="/mail/mailboxes" replace /> },
			{ path: "/aliases", element: <Navigate to="/mail/aliases" replace /> },
			{ path: "/forwarders", element: <Navigate to="/mail/forwarders" replace /> },
			{ path: "/migration", element: <Navigate to="/mail/migration" replace /> },
			{ path: "/billing", element: <Navigate to="/manage/billing" replace /> },
			{ path: "/settings/team", element: <Navigate to="/manage/access" replace /> },
			{ path: "/settings/organisation", element: <Navigate to="/manage/config" replace /> },
			{ path: "/settings/*", element: <Legacy to="/manage/account" /> },
			{ path: "/settings", element: <Navigate to="/manage/config" replace /> },
			{ path: "/support", element: <Navigate to="/manage" replace /> },
			{ path: "/manage/mailboxes/*", element: <Legacy to="/mail/mailboxes" /> },
			{ path: "/manage/mailboxes", element: <Navigate to="/mail/mailboxes" replace /> },
			{ path: "/manage/aliases", element: <Navigate to="/mail/aliases" replace /> },
			{ path: "/manage/forwarders", element: <Navigate to="/mail/forwarders" replace /> },
			{ path: "/manage/migration", element: <Navigate to="/mail/migration" replace /> },
			{ path: "/manage/settings/team", element: <Navigate to="/manage/access" replace /> },
			{ path: "/manage/settings", element: <Navigate to="/manage/config" replace /> },
			{ path: "/manage/support", element: <Navigate to="/manage" replace /> },
			{ path: "/dash", element: <Navigate to="/manage/platform" replace /> },
			{ path: "/dash/mail-health", element: <Navigate to="/manage/platform/mail/health" replace /> },
			{ path: "/dash/queues", element: <Navigate to="/manage/platform/mail/queues" replace /> },
			{ path: "/dash/abuse", element: <Navigate to="/manage/platform/mail/abuse" replace /> },
			{ path: "/dash/audit", element: <Navigate to="/manage/platform/audit" replace /> },
			{ path: "/dash/configuration", element: <Navigate to="/manage/platform/mail/server" replace /> },
			{ path: "/dash/support", element: <Navigate to="/manage" replace /> },
			{ path: "/dash/incidents", element: <Navigate to="/manage" replace /> },
			{ path: "*", element: <NotFound />, ...handle("Not found") },
		],
	},
], basename ? { basename } : undefined);
